// =============================================================================
// MOP PDF export — T-MOP
// =============================================================================
// Portrait A4, Coretel-branded, controlled-document framing — the document a
// crew works to on site and a client HSE manager is handed afterwards.
// Everything printed comes from the frozen inputs (see mopTemplate.ts): the
// PDF layer formats, it never invents. pdfmake lazy-load pattern and header/
// footer conventions cribbed from swmsPdf (T-427b).
//
// Regeneration promise: an approved version renders from its mop_versions
// snapshot, so "re-download rev 2" produces the same document next year.
// =============================================================================

import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore vfs fonts has no types
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";
import {
  type MopInputs, CRANE_SETUP_WORDING, SUSPENDED_LOADS_WORDING, PAINTED_STRUCTURE_WORDING,
} from "./mopTemplate";

const C = {
  accent: "#FF6A1A",
  ink: "#111111",
  text: "#222222",
  muted: "#666666",
  panel: "#F6F6F4",
  rule: "#E2E1DD",
  warn: "#CB4335",
};

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else { existing.addEventListener("load", () => resolve()); existing.addEventListener("error", () => reject(new Error(`reload ${tag}`))); }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`load ${tag}`));
    document.head.appendChild(s);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("pdfMake missing");
  return pdfMake;
}

// Logo loader — same pattern as quotePdf (T-395): fetch from the app's
// public/ and inline as a data URL; the PDF still renders if it fails.
async function fetchLogoDataUrl(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

const label = (t: string) => ({ text: t, bold: true, fontSize: 7, color: C.muted });
const val = (t: string) => ({ text: t || "—", fontSize: 8.5 });
const panelLayout = {
  fillColor: () => C.panel, hLineWidth: () => 0, vLineWidth: () => 0,
  paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6,
};
const ruledLayout = {
  hLineWidth: () => 0.5, vLineWidth: () => 0.5,
  hLineColor: () => C.rule, vLineColor: () => C.rule,
  paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6,
};
// 21 Sep 2026 (Projects brief B2): pdfmake's bundled Roboto has no arrow
// glyph, so "guy wire → rope grab → load cell" printed as a run of nouns. The
// arrows carry the sequence — swap them for " > " rather than lose them.
// ± ° × Ø are in the font and pass through untouched.
export function pdfSafe(s: string | null | undefined): string {
  return (s ?? "").replace(/\s*[→⟶➜➔⇒]\s*/g, " > ").replace(/\s*←\s*/g, " < ").replace(/[↔⇔]/g, " <> ");
}

// A step's details, laid out the way the field instruction was written rather
// than as one run-on paragraph. WHO / DO / CHECK / STOP IF lead their own line
// in bold; numbered actions sit one per line, indented; STOP IF is accented so
// the abnormal case is findable at a glance at an anchor. A block of prose with
// none of those labels simply prints as prose.
const DETAIL_LABEL = /^(WHO|DO|CHECK|STOP IF|WHY[A-Z ]*|FIRST[A-Z —-]*|CONTROL NOTES?)\s*[:—]\s*(.*)$/i;
function detailLines(details: string): any[] {
  const out: any[] = [];
  for (const raw of details.split("\n")) {
    const l = raw.trim();
    if (!l) continue;
    const m = l.match(DETAIL_LABEL);
    if (m) {
      const stop = /^stop if/i.test(m[1]);
      out.push({
        text: [
          { text: m[1].toUpperCase() + ": ", bold: true, color: stop ? C.warn : C.muted },
          { text: m[2], color: stop ? C.warn : C.text },
        ],
        fontSize: 8.5, lineHeight: 1.2, margin: [0, 3, 0, 0],
      });
      continue;
    }
    const num = l.match(/^(\d+[.)])\s*(.+)$/);
    if (num) {
      out.push({ text: `${num[1]} ${num[2]}`, fontSize: 8.5, lineHeight: 1.2, margin: [10, 1, 0, 0] });
      continue;
    }
    out.push({ text: l, fontSize: 8.5, lineHeight: 1.2, margin: [0, 3, 0, 0] });
  }
  return out;
}

function section(t: string) {
  return {
    text: t.toUpperCase(), bold: true, fontSize: 9.5, color: C.accent,
    characterSpacing: 0.8, margin: [0, 14, 0, 5],
  };
}

export async function exportMopPdf(opts: {
  mopNumber: string;
  title: string;
  version: number;
  status: string;                 // draft | approved | superseded
  projectCode?: string | null;
  projectName?: string | null;
  siteName?: string | null;
  siteAddress?: string | null;
  clientName?: string | null;
  preparedBy?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  inputs: MopInputs;
  // Approved revision history for the Document Control table.
  versions?: { version: number; approved_at?: string | null; approved_by?: string | null }[];
  // Pre-resolved data URLs (the screen signs + fetches from hse-evidence) —
  // this module stays free of storage concerns.
  layoutImageDataUrl?: string | null;
  forecastImageDataUrl?: string | null;
  stepImageDataUrls?: Record<number, string>;
}) {
  const pdfMake = await getPdfMake();
  const logoDataUrl = await fetchLogoDataUrl("/coretel-logo-full.png");
  const inp = opts.inputs;
  const approved = opts.status === "approved";
  // pdfmake's bundled Roboto has no arrow glyphs — "NBN → Ventia" prints as
  // boxes. Swap arrows for ">" in the one field people type them into.
  const deArrow = (t: string) => t.replace(/[→⇒➔›]/g, ">");
  const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtDay = (d?: string | null) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const content: any[] = [];

  // ---- cover page ------------------------------------------------------------
  content.push({ text: " ", margin: [0, 110, 0, 0] });
  content.push({ text: "METHOD OF PROCEDURE", fontSize: 30, bold: true, color: C.accent, alignment: "center" });
  content.push({ text: "(MOP)", fontSize: 30, bold: true, color: C.accent, alignment: "center", margin: [0, 0, 0, 22] });
  content.push({ text: opts.title, fontSize: 15, bold: true, color: C.ink, alignment: "center", margin: [0, 0, 0, 8] });
  content.push({
    text: approved ? `${opts.mopNumber} · Rev ${opts.version} · APPROVED` : `${opts.mopNumber} · DRAFT — NOT APPROVED FOR USE`,
    fontSize: 11, bold: true, color: approved ? C.muted : C.warn, alignment: "center", margin: [0, 0, 0, 30],
  });
  const coverRow = (k: string, v: string) => ({ text: [{ text: `${k}: `, bold: true }, { text: v || "—" }], fontSize: 10, margin: [110, 0, 0, 5] });
  content.push(coverRow("Site", [opts.siteName, opts.siteAddress].filter(Boolean).join(" · ") || "—"));
  content.push(coverRow("Client", deArrow(inp.meta.client_chain || opts.clientName || "—")));
  content.push(coverRow("Date of works", fmtDay(inp.meta.mop_date)));
  content.push(coverRow("Project", `${opts.projectCode ?? "—"} · ${opts.projectName ?? ""}`));
  content.push(coverRow("Contractor", "Coretel Group"));
  if (logoDataUrl) {
    content.push({ image: logoDataUrl, width: 170, alignment: "center", margin: [0, 44, 0, 0] });
  }
  content.push({ text: "", pageBreak: "after" });

  // ---- title + identity panel ----------------------------------------------
  content.push({ text: opts.title, bold: true, fontSize: 15, color: C.ink, margin: [0, 0, 0, 6] });
  content.push({
    table: {
      widths: ["auto", "*", "auto", "*"],
      body: [
        [label("PROJECT"), val(`${opts.projectCode ?? "—"} · ${opts.projectName ?? ""}`),
         label("CLIENT CHAIN"), val(deArrow(inp.meta.client_chain || opts.clientName || "—"))],
        [label("SITE"), val([opts.siteName, opts.siteAddress].filter(Boolean).join(" · ") || "—"),
         label("WORK DATE"), val(fmtDay(inp.meta.mop_date))],
        [label("PREPARED BY"), val(opts.preparedBy ?? "—"),
         label("APPROVAL"), approved
           ? val(`APPROVED v${opts.version} · ${opts.approvedBy ?? ""} · ${fmt(opts.approvedAt)}`)
           : { text: "DRAFT — NOT FOR USE", fontSize: 8.5, bold: true, color: C.warn }],
      ],
    },
    layout: panelLayout,
  });

  // ---- document control -------------------------------------------------------
  content.push(section("Document control"));
  {
    const rows: any[] = [[label("REVISION"), label("DATE"), label("APPROVED BY")]];
    (opts.versions ?? []).forEach(v => rows.push([
      { text: `Rev ${v.version}`, fontSize: 8.5 },
      { text: fmt(v.approved_at), fontSize: 8.5 },
      { text: v.approved_by ?? "—", fontSize: 8.5 },
    ]));
    if (!approved) rows.push([
      { text: "Draft (this print)", fontSize: 8.5, color: C.warn },
      { text: fmt(new Date().toISOString()), fontSize: 8.5 },
      { text: "—", fontSize: 8.5 },
    ]);
    content.push({ table: { widths: [90, 110, "*"], body: rows, dontBreakRows: true, headerRows: 1 }, layout: ruledLayout });
  }

  // ---- scope -----------------------------------------------------------------
  content.push(section("Scope of work"));
  // (all free text below passes through pdfSafe — see the note on it)
  content.push({ text: pdfSafe(inp.scope) || "—", fontSize: 9 });
  if (inp.painted_structure) {
    content.push({
      margin: [0, 6, 0, 0],
      table: { widths: ["*"], body: [[{ text: PAINTED_STRUCTURE_WORDING, fontSize: 8.5, bold: true }]] },
      layout: { ...panelLayout, fillColor: () => "#FDF2E9" },
    });
  }

  // ---- crew ------------------------------------------------------------------
  if (inp.meta.crew.length > 0) {
    content.push(section("Personnel"));
    content.push({
      table: { widths: ["*"], body: inp.meta.crew.map(cw => [{ text: cw, fontSize: 8.5 }]), dontBreakRows: true },
      layout: ruledLayout,
    });
  }

  // ---- plant & equipment -----------------------------------------------------
  content.push(section("Plant & equipment"));
  {
    const rows: any[] = [[
      { text: "ITEM", bold: true, fontSize: 7, color: C.muted },
      { text: "DETAILS", bold: true, fontSize: 7, color: C.muted },
    ]];
    if (inp.crane) {
      // Config labels usually already carry counterweight + outriggers
      // ("Main boom T · 20t CW · 7.00 × 7.50m outriggers") — only append the
      // separate fields when the label doesn't, so the row never says it twice.
      const cfgLower = (inp.crane.config_label ?? "").toLowerCase();
      rows.push([
        { text: `Crane — ${inp.crane.model_label}`, fontSize: 8.5, bold: true },
        { text: [
            inp.crane.config_label,
            inp.crane.counterweight_t != null && !/\bcw\b|counterweight/.test(cfgLower)
              ? `${inp.crane.counterweight_t} t counterweight` : null,
            inp.crane.outriggers && !cfgLower.includes("outrigger")
              ? `outriggers ${inp.crane.outriggers}` : null,
            inp.crane.chart_wind_ms != null ? `chart wind limit ${inp.crane.chart_wind_ms} m/s` : null,
            inp.crane.chart_basis === "machine" ? "machine chart held in Coretel crane library" : "generic chart in Coretel crane library — machine chart to be sighted before lifting",
          ].filter(Boolean).join(" · "), fontSize: 8.5 },
      ]);
    }
    inp.plant.forEach(p => rows.push([{ text: pdfSafe(p.item), fontSize: 8.5 }, { text: pdfSafe(p.details), fontSize: 8.5 }]));
    if (rows.length === 1) rows.push([val("—"), val("")]);
    content.push({ table: { widths: ["auto", "*"], body: rows, dontBreakRows: true, headerRows: 1 }, layout: ruledLayout });
  }

  // ---- reference documents ---------------------------------------------------
  content.push(section("Reference documents"));
  {
    const kindLabel: Record<string, string> = {
      lift_study: "Lift study", erection_drawings: "Erection drawings",
      layout_image: "Site layout", other: "Reference",
    };
    const rows: any[] = [[
      { text: "DOCUMENT", bold: true, fontSize: 7, color: C.muted },
      { text: "DESCRIPTION", bold: true, fontSize: 7, color: C.muted },
    ]];
    inp.refs.forEach(r => rows.push([
      { text: `${kindLabel[r.kind] ?? "Reference"} — ${r.file_name}`, fontSize: 8.5 },
      { text: r.description, fontSize: 8.5 },
    ]));
    if (rows.length === 1) rows.push([val("—"), val("")]);
    content.push({ table: { widths: ["auto", "*"], body: rows, dontBreakRows: true, headerRows: 1 }, layout: ruledLayout });
    content.push({
      text: "Attachments are held against this MOP in the Coretel operations system and form part of this document.",
      fontSize: 7, color: C.muted, margin: [0, 3, 0, 0],
    });
  }

  // ---- site conditions & crane setup (FIXED wording + site notes) ------------
  // 21 Sep 2026 — a MOP with no crane (Willowvale guy re-tension) prints
  // "Site conditions" with the site notes only; the fixed crane-setup wording
  // is for crane MOPs and would read as nonsense on a job with no lift.
  if (inp.crane) {
    content.push(section("Site conditions & crane setup"));
    content.push({ text: CRANE_SETUP_WORDING, fontSize: 9 });
    if (inp.site_conditions_extra.trim()) {
      content.push({ text: pdfSafe(inp.site_conditions_extra.trim()), fontSize: 9, margin: [0, 5, 0, 0] });
    }
  } else {
    content.push(section("Site conditions"));
    content.push({ text: pdfSafe(inp.site_conditions_extra.trim()) || "No site conditions recorded.", fontSize: 9 });
  }

  // ---- working under suspended loads ----------------------------------------
  if (inp.suspended_loads) {
    // Unbreakable: a page break through this table strands one control line on
    // its own on the next page (James, 11 Sep: "not one line in a table on the
    // next page"). The block is short — move it whole or not at all.
    content.push({
      unbreakable: true,
      stack: [
        section("Working under suspended loads — controls"),
        {
          table: { widths: ["*"], body: SUSPENDED_LOADS_WORDING.map(w => [{ text: w, fontSize: 8.5 }]) },
          layout: ruledLayout,
        },
      ],
    });
  }

  // ---- environmental ----------------------------------------------------------
  content.push(section("Environmental limits"));
  content.push({
    text: inp.crane
      ? (inp.environmental.wind_limit_ms != null
          ? `Lifting ceases at a sustained wind of ${inp.environmental.wind_limit_ms} m/s at hook height, or earlier at the crane operator's or lift supervisor's discretion. The crane chart's wind limit governs if lower.`
          : "Wind limit per the attached lift study and the crane chart; the crane operator's or lift supervisor's call stands at any time.")
      : (inp.environmental.wind_limit_ms != null
          ? `Work ceases at a sustained wind of ${inp.environmental.wind_limit_ms} m/s, or earlier at the site supervisor's discretion.`
          : "WIND LIMIT — NOT SET. This MOP cannot be issued until a working wind limit is stated and its source recorded. The site supervisor's call to stop work stands at any time, in addition to — never instead of — a stated limit."),
    bold: inp.environmental.wind_limit_ms == null && !inp.crane,
    color: inp.environmental.wind_limit_ms == null && !inp.crane ? C.accent : C.text,
    fontSize: 9,
  });
  if (inp.environmental.notes.trim()) {
    content.push({ text: pdfSafe(inp.environmental.notes.trim()), fontSize: 9, margin: [0, 4, 0, 0] });
  }
  // Forecast snip for the work date — Beechmont Rev 3 pattern (Windy/BoM
  // screenshot sat under the wind paragraph). Planning context only; the
  // wind-limit wording above stays the operative control.
  if (opts.forecastImageDataUrl) {
    content.push({ image: opts.forecastImageDataUrl, fit: [440, 250], margin: [0, 6, 0, 0] });
    content.push({
      text: "Forecast at time of issue — conditions on the day are confirmed at the pre-start briefing; the wind limit above governs.",
      fontSize: 7, color: C.muted, margin: [0, 2, 0, 0],
    });
  }

  // ---- site layout image ------------------------------------------------------
  if (opts.layoutImageDataUrl) {
    // The layout is the picture the crew actually reads — let it take the room
    // (James, 11 Sep: "take more room on this photo"). Full content width,
    // most of a page tall; pdfmake's fit keeps the aspect ratio.
    // Projects brief 2, B1 (23 Sep 2026): the heading read "laydown & lift
    // path" on a MOP whose crane is "No crane on this MOP" — there is no lift,
    // no lift path and no laydown on a ground-level guy re-tension. Same family
    // as the crane seed steps: crane assumptions baked into fixed wording. The
    // heading now follows the crane selection.
    // 23 Sep 2026 — the layout gets a PAGE OF ITS OWN. First it was heading and
    // image pushed separately, so the picture jumped the page break and left
    // the heading stranded. Then they moved together, which fixed the orphan
    // but still let whatever came next start in the strip under the image —
    // the guy schedule began with a single row at the foot of the page.
    // James: "may as well make this a full page to stop the below table coming
    // up … safer I guess to have this as a bit of a page break." He is right:
    // it is a drawing a crew holds up and reads against the site, so it earns
    // the page, and nothing can nest awkwardly beneath it.
    content.push({ text: "", pageBreak: "before" });
    content.push(section(inp.crane ? "Site layout — laydown & lift path" : "Site layout"));
    content.push({ image: opts.layoutImageDataUrl, fit: [531, 660], margin: [0, 2, 0, 0] });
    // Close the page only when something would otherwise follow it here. The
    // method already breaks before itself, so adding a break with no blocks in
    // between would leave a blank page.
    if ((inp.blocks ?? []).some(b => (b.title ?? "").trim() || (b.body ?? "").trim())) {
      content.push({ text: "", pageBreak: "after" });
    }
  }

  // ---- document blocks (23 Sep 2026, Projects brief 2 A2) ---------------------
  // Rules that govern every step without being one — the communications
  // protocol, permit or client conditions, emergency arrangements. They print
  // immediately before the method, because the method refers to them: a step
  // saying "per the call protocol" is worse than useless if the protocol is not
  // in the document the crew is holding. Line breaks are kept as written.
  for (const b of inp.blocks ?? []) {
    const title = pdfSafe((b.title ?? "").trim());
    const body = pdfSafe((b.body ?? "").trim());
    if (!title && !body) continue;
    content.push(section(title || "Standing rules for this job"));
    // A block can carry a table — the guy schedule is one, and the file that
    // wrote it says "Print this table in the MOP", because every reading the
    // method takes is recorded against a wire reference in it. Markdown pipe
    // rows become a real table; everything else prints as written.
    let run: string[][] = [];
    const flushTable = () => {
      if (run.length === 0) return;
      const cols = Math.max(...run.map(r => r.length));
      const body2 = run.map((r, i) => {
        const cells = Array.from({ length: cols }, (_, c) => r[c] ?? "");
        return cells.map(t => i === 0 ? label(t) : { text: t, fontSize: 8.5 });
      });
      // 23 Sep 2026 — a table used to split wherever the page ran out, so the
      // guy schedule started with ONE row at the foot of page 4 and carried a
      // half-row across the break (James: "shouldn't start a table with only
      // like 1 row, put it on the next page"). dontBreakRows keeps a row whole;
      // a table short enough to fit a page moves to the next page intact
      // rather than leaving an orphan behind. A genuinely long table still
      // breaks — with its header repeated — because it has to.
      const tbl: any = {
        table: { headerRows: 1, widths: Array.from({ length: cols }, () => "*"), body: body2, dontBreakRows: true },
        layout: ruledLayout, margin: [0, 2, 0, 4],
      };
      content.push(run.length <= 20 ? { unbreakable: true, stack: [tbl] } : tbl);
      run = [];
    };
    for (const raw of body.split("\n")) {
      const l = raw.trim();
      if (l.startsWith("|")) { run.push(l.split("|").slice(1, -1).map(c => c.trim())); continue; }
      flushTable();
      if (!l) { content.push({ text: " ", fontSize: 4 }); continue; }
      content.push({ text: l, fontSize: 9, lineHeight: 1.25, margin: [0, 0, 0, 1] });
    }
    flushTable();
  }

  // ---- method: ONE table, Beechmont style — Step | Action | Details ----------
  content.push({ text: "", pageBreak: "before" });
  content.push(section("Method"));
  {
    const rows: any[] = [[label("STEP"), label("ACTION"), label("DETAILS")]];
    inp.method.forEach((st, i) => {
      const details = pdfSafe((st.details_pro || st.details || "").trim());
      const cellStack: any[] = [];
      // 21 Sep 2026 (Projects brief A3): a HOLD POINT is where the method
      // deliberately stops. It prints boxed and shaded, with the condition to
      // proceed and a line for who cleared it — never as one more step.
      if (st.hold) cellStack.push({ text: "HOLD POINT — DO NOT PROCEED PAST THIS STEP UNTIL CLEARED", bold: true, fontSize: 8.5, color: C.accent });
      if (st.action?.trim()) cellStack.push({ text: (st.hold ? "Proceed only when: " : "") + pdfSafe(st.action.trim()), fontSize: 8.5, bold: !!st.hold, margin: [0, st.hold ? 2 : 0, 0, 0] });
      // 23 Sep 2026 — THE DETAILS NEVER PRINTED. `details` was computed on the
      // line above and then never pushed into the cell, so every MOP printed
      // only its one-line action: the WHO / DO / CHECK / STOP IF block, and
      // everything a manager typed into the details field, was dropped on the
      // way to the page. That is why the document read thin — the detail was
      // in the builder the whole time. James, 23 Sep: "the details are kind of
      // there but the wording is all off a bit."
      if (details) {
        const ds = detailLines(details);
        if (ds.length) ds[0] = { ...ds[0], margin: [(ds[0].margin?.[0] ?? 0), 4, 0, 0] };
        cellStack.push(...ds);
      }
      const img = opts.stepImageDataUrls?.[i];
      if (img) cellStack.push({ image: img, fit: [330, 210], margin: [0, 5, 0, 2] });
      if (st.hold) cellStack.push({ text: "Cleared by: ______________________   Role: ______________   Date / time: ______________", fontSize: 8, color: C.muted, margin: [0, 6, 0, 0] });
      if (cellStack.length === 0) cellStack.push({ text: "—", fontSize: 8.5, color: C.muted });
      const fill = st.hold ? "#FDECE4" : undefined;
      rows.push([
        { text: String(i + 1), fontSize: 9, alignment: "center", fillColor: fill },
        { text: pdfSafe(st.title) || "—", fontSize: 8.5, bold: true, fillColor: fill, color: st.hold ? C.accent : C.text },
        { stack: cellStack, fillColor: fill },
      ]);
    });
    content.push({
      table: { widths: [24, 158, "*"], body: rows, dontBreakRows: true, headerRows: 1 },
      layout: ruledLayout,
    });
  }

  // ---- still open (Projects brief D5 / F) ---------------------------------------
  // The printed document must be at least as honest as the form: everything
  // the MOP does not yet settle prints here, on every copy, until it closes.
  const openItems = (inp.open_items ?? []).map(s => s.trim()).filter(Boolean);
  if (openItems.length) {
    content.push({
      unbreakable: true,
      stack: [
        section("Still open — this MOP does not yet settle these"),
        {
          table: { widths: [18, "*"], body: openItems.map((o, i) => [{ text: String(i + 1), fontSize: 8.5, color: C.muted, alignment: "center" }, { text: pdfSafe(o), fontSize: 8.5 }]) },
          layout: ruledLayout,
        },
        { text: "A draft carries these so whoever picks it up sees what is not settled. They are closed in the operations system before the MOP is issued.", fontSize: 7.5, color: C.muted, margin: [0, 3, 0, 0] },
      ],
    });
  }

  // ---- don'ts — the standing rules for this job (backbone Appendix B) --------
  const donts = (inp.donts ?? []).map(d => d.trim()).filter(Boolean);
  if (donts.length) {
    content.push({
      unbreakable: true,
      stack: [
        section("Don'ts — standing rules for this job"),
        {
          table: { widths: ["*"], body: donts.map(d => [{ text: pdfSafe(d), fontSize: 8.5 }]) },
          layout: ruledLayout,
        },
      ],
    });
  }

  // ---- sign-on ----------------------------------------------------------------
  // Unbreakable so the block never strands a few empty rows on a page of their
  // own — if it doesn't fit below the method, the whole table moves together.
  content.push({
    unbreakable: true,
    stack: [
      section("Sign-on — I have read and understood this MOP"),
      {
        table: {
          widths: ["*", "*", "auto", "auto"],
          body: [
            [label("NAME"), label("COMPANY / ROLE"), label("SIGNATURE"), label("DATE")],
            ...Array.from({ length: 10 }, () => [
              { text: " ", margin: [0, 7, 0, 7] }, " ", { text: " ", margin: [40, 0, 40, 0] }, { text: " ", margin: [24, 0, 24, 0] },
            ]),
          ],
        },
        layout: ruledLayout,
      },
    ],
  });

  const dd: any = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [32, 64, 32, 42],
    defaultStyle: { fontSize: 9, color: C.text },
    header: {
      margin: [32, 18, 32, 0],
      columns: [
        {
          stack: [
            { text: "CORETEL GROUP", bold: true, fontSize: 13, color: C.ink, characterSpacing: 0.5 },
            { text: "METHOD OF PROCEDURE", fontSize: 7.5, color: C.accent, characterSpacing: 1.5 },
          ],
        },
        {
          stack: [
            { text: opts.mopNumber, alignment: "right", bold: true, fontSize: 10, color: C.accent },
            {
              text: approved ? `APPROVED · v${opts.version}` : "DRAFT — NOT FOR USE",
              alignment: "right", fontSize: 7.5, bold: !approved,
              color: approved ? C.muted : C.warn,
            },
          ],
        },
      ],
    },
    footer: (page: number, pages: number) => ({
      margin: [32, 8, 32, 0],
      columns: [
        { text: "Printed/downloaded copies are UNCONTROLLED — the current version lives in the Coretel operations system.", fontSize: 6.5, color: C.muted },
        { text: `Page ${page} of ${pages}`, alignment: "right", fontSize: 7, color: C.muted },
      ],
    }),
    // Draft watermark — the same discipline as the on-screen pill: nobody
    // works to an unapproved MOP without knowing it.
    ...(approved ? {} : { watermark: { text: "DRAFT", color: C.warn, opacity: 0.06, bold: true } }),
    content,
  };

  pdfMake.createPdf(dd).download(
    `${opts.mopNumber}_${(opts.title || "MOP").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 60)}_v${opts.version}${approved ? "" : "_DRAFT"}.pdf`,
  );
}
